import React, {Suspense, useEffect} from "react";
import {createBrowserRouter, useLocation, useNavigate, useParams} from "react-router-dom";
import {LanguageRedirect} from "i18n/LanguageRedirect";
import {useLocalizedNavigate} from "i18n/hooks";
import {Outlet} from "react-router-dom";
import {getInitialLanguage, getLanguage, LanguageBoundary, LanguageProvider} from "i18n/LanguageProvider";
import {isLanguagePrefixedRoute, isSupportedLanguage, isWidgetRoute, LoadingFallback} from "utils";
import {AuthGuard} from "components/AuthGuard";
import {AUTH_ONLY} from "hooks/useAuth";
import {LoginCompleteHandler} from "hooks/LoginCompleteHandler";
import {GuestCheckoutReturnHandler} from "hooks/GuestCheckoutReturnHandler";
import {AccountLanguageSync} from "hooks/AccountLanguageSync";

// Static import — always needed for main routes, no benefit from lazy-loading
import {RootComponent} from "Application/RootComponent";
import {ErrorPage} from "Application/errors";
const AuthCallbackPage = React.lazy(() => import("pages/AuthCallback").then(m => ({default: m.AuthCallbackPage})));

// Eager preload critical-path chunks at module parse time for non-widget routes.
// All three start downloading in PARALLEL instead of sequentially via Suspense waterfall.
const isWidget = isWidgetRoute();

// Falling back to a plain import keeps these renderable even when the preload was skipped —
// the widget route does not render them, but a null promise here would crash instead of load.
const mainPagePromise = isWidget ? null : import("Application/MainPage");
const MainPage = React.lazy(() => (mainPagePromise ?? import("Application/MainPage")).then(m => ({default: m.MainPage})));

const scanPagePromise = isWidget ? null : import("pages/ScanPage");
const ScanPage = React.lazy(() => (scanPagePromise ?? import("pages/ScanPage")).then(m => ({default: m.ScanPage})));

// Lazy-loaded page components
const SubscriptionPage = React.lazy(() => import("pages/SubscriptionPage").then(m => ({default: m.SubscriptionPage})));
const Statistics = React.lazy(() => import("pages/StatisticsPage").then(m => ({default: m.Statistics})));
const APITable = React.lazy(() => import("pages/ApiPage").then(m => ({default: m.APITable})));
const HistoryRoute = React.lazy(() => import("pages/HistoryPage").then(m => ({default: m.HistoryRoute})));
const ExtensionLogin = React.lazy(() => import("pages/extensionLogin").then(m => ({default: m.ExtensionLogin})));
const Sandbox = React.lazy(() => import("pages/SandboxPage").then(m => ({default: m.Sandbox})));
const ReportPage = React.lazy(() => import("pages/ReportPage").then(m => ({default: m.ReportPage})));
const MoodleStatisticPage = React.lazy(() => import("pages/MoodleStatistic").then(m => ({default: m.MoodleStatisticPage})));
const ScanWidgetPage = React.lazy(() => import("pages/ScanWidget").then(m => ({default: m.ScanWidgetPage})));
const CancelFeedbackPage = React.lazy(() => import("pages/CancelFeedback").then(m => ({default: m.CancelFeedbackPage})));

const OpenDocs = () => {
    const navigate = useLocalizedNavigate();
    useEffect(() => {
        window.open(process.env.REACT_APP_DOCS_URL)
        navigate('/main/api')
    })
    return <></>
}

const BareRouteRedirect = ({children}: any) => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {

        // No language in the URL — the only spot where a first-time visitor's
        // device locale may decide (see getInitialLanguage).
        const targetLang = getInitialLanguage(location.search)

        const currentPath = location.pathname;
        const pathWithLang = `/${targetLang}${currentPath}`;
        const targetPath = isLanguagePrefixedRoute(currentPath) ? currentPath : pathWithLang;
        const targetRoute = `${targetPath}${location.search ?? ""}`;

        console.log("Base redirect to ", targetRoute)
        // Navigate to the language-prefixed route
        navigate(targetRoute, {replace: true});
    }, [location.pathname, location.search, navigate]);

    return children;
};

export const LanguageLayout: React.FC = () => {

    const params = useParams();
    if (!isSupportedLanguage(params.lang)) {
        return <BareRouteRedirect/>
    }

    const langParam = params.lang;
    const lang = getLanguage(langParam);
    return (
        <LanguageProvider lang={lang}>
            {!isWidgetRoute() && <LoginCompleteHandler/>}
            {!isWidgetRoute() && <GuestCheckoutReturnHandler/>}
            {!isWidgetRoute() && <AccountLanguageSync/>}
            <Outlet/>
        </LanguageProvider>
    );
};

// Every page renders through here, so this is where a language switch remounts them.
const S = ({children, loader=true}: {children: React.ReactNode, loader?: boolean}) =>
    <LanguageBoundary>
        <Suspense fallback={loader?<LoadingFallback/>: null}>{children}</Suspense>
    </LanguageBoundary>;

// An account-only page: an anon gets the login, and a logout here leaves for the main page (useMyLogout).
const authOnly = (page: React.ReactNode) => ({
    handle: AUTH_ONLY,
    element: <S><MainPage><AuthGuard>{page}</AuthGuard></MainPage></S>,
});

export const appRouter = createBrowserRouter([
    {
        path: "/:lang",
        element: <LanguageLayout/>,
        errorElement: <ErrorPage/>,
        children: [
            {
                path: 'report/:id/ai',
                element: <S><ReportPage/></S>
            },
            {
                path: 'report/:id',
                element: <S><ReportPage/></S>
            },
            {
                path: 'report/:id/plagiarism',
                element: <S><ReportPage/></S>
            },
            {
                path: 'moodle/statistics/:uuid',
                element: <S><MoodleStatisticPage/></S>
            },
            {
                path: 'widgets/scan',
                element: <S><ScanWidgetPage/></S>
            },
            {
                path: 'auth/callback',
                element: <S><AuthCallbackPage/></S>
            },
            {
                // Opened from the cancellation email. Deliberately outside
                // RootComponent: it needs no session, and the reader who clicks
                // it usually has none on that device.
                path: 'feedback/cancel',
                element: <S><CancelFeedbackPage/></S>
            },
            {
                path: "",
                element: <RootComponent/>,
                children: [
                    {
                        path: 'main/scan',
                        element: <S><MainPage><ScanPage/></MainPage></S>,
                    },
                    {
                        path: 'main/api',
                        element: <LanguageRedirect to="/main/api/table"/>
                    },
                    {
                        path: 'main/subscription',
                        element: <S><MainPage><SubscriptionPage/></MainPage></S>
                    },
                    {
                        path: 'main/statistics',
                        ...authOnly(<Statistics/>),
                    },
                    {
                        path: 'main/api/table',
                        ...authOnly(<APITable/>),
                    },
                    {
                        path: 'main/api/docs',
                        element: <OpenDocs/>
                    },

                    {
                        path: 'main/history',
                        // Not authOnly: an anon gets the page's own login prompt in place of the list.
                        element: <S><MainPage><HistoryRoute/></MainPage></S>,
                    },
                    {
                        path: 'extension',
                        element: <S><ExtensionLogin/></S>
                    },
                    {
                        path: 'sandbox',
                        element: <S><Sandbox/></S>
                    },
                    // Any unknown route
                    {
                        path: '*',
                        element: <LanguageRedirect to="/main/scan"/>
                    },
                    // Just slash at the end
                    {
                        index: true,
                        element: <LanguageRedirect to="/main/scan"/>
                    },
                ]
            }
        ]
    },
    {
        path: '*',
        element: <BareRouteRedirect/>,
        errorElement: <ErrorPage/>
    }
])
